import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import LedgerPage from './pages/LedgerPage';
import TenantLoginPage from './pages/TenantLoginPage';
import TenantInvitePage from './pages/TenantInvitePage';
import TenantPortalPage from './pages/TenantPortalPage';
import TenantForgotPasswordPage from './pages/TenantForgotPasswordPage';
import TenantResetPasswordPage from './pages/TenantResetPasswordPage';
import TenantConsentPage from './pages/TenantConsentPage';
import TenantSettingsPage from './pages/TenantSettingsPage';
import AccountPage from './pages/AccountPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

function RequireLandlord({ children }) {
  const { user, isLandlord } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isLandlord) return <Navigate to="/tenant/portal" replace />;
  return children;
}

function RequireTenant({ children }) {
  const { user, isTenant } = useAuth();
  if (!user) return <Navigate to="/tenant/login" replace />;
  if (!isTenant) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user, isLandlord, isTenant } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      {/* Landlord auth */}
      <Route path="/register" element={
        user ? <Navigate to="/dashboard" replace /> : <RegisterPage />
      } />
      <Route path="/login" element={
        user ? <Navigate to="/dashboard" replace /> : <LoginPage />
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Landlord app */}
      <Route path="/dashboard" element={
        <RequireLandlord><DashboardPage /></RequireLandlord>
      } />
      <Route path="/properties/:id" element={
        <RequireLandlord><LedgerPage /></RequireLandlord>
      } />
      <Route path="/account" element={
        <RequireLandlord><AccountPage /></RequireLandlord>
      } />

      {/* Tenant */}
      <Route path="/tenant/login" element={
        isTenant ? <Navigate to="/tenant/portal" replace /> : <TenantLoginPage />
      } />
      <Route path="/tenant/invite/:token" element={<TenantInvitePage />} />
      <Route path="/tenant/portal" element={
        <RequireTenant><TenantPortalPage /></RequireTenant>
      } />
      <Route path="/tenant/consent" element={
        <RequireTenant><TenantConsentPage /></RequireTenant>
      } />
      <Route path="/tenant/settings" element={
        <RequireTenant><TenantSettingsPage /></RequireTenant>
      } />
      <Route path="/tenant/forgot-password" element={<TenantForgotPasswordPage />} />
      <Route path="/tenant/reset-password/:token" element={<TenantResetPasswordPage />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
